import React, { useEffect, useState } from 'react';
import { Spinner, Nav } from 'react-bootstrap';
import axios from 'axios';
import SingleElimBracketView from '../Bracket/SingleElimBracketView';
import ThreeGGBracketView from '../Bracket/ThreeGGBracketView';

const Url = process.env.REACT_APP_MAIN_URL;

/**
 * Read-only bracket display for the public tournament page.
 *
 * Reuses the exact same views the admin sees. Passing isAdmin={false} is what
 * makes them read-only — the views already hide Publish / Regenerate / Delete,
 * the ✎ schedule button, and score entry when it's false. There's no separate
 * "public" rendering to drift out of sync with the real one.
 *
 * The endpoint only ever returns published brackets, so drafts can't appear
 * here even if someone guesses a division id.
 */
const PublicBracketsTab = ({ divisionId, refreshTrigger }) => {
  const [brackets, setBrackets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (divisionId) fetchBrackets();
  }, [divisionId, refreshTrigger]);

  const fetchBrackets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${Url}api/public/brackets/division/${divisionId}`
      );
      if (response.data.success) {
        const list = response.data.data || [];
        setBrackets(list);
        if (list.length > 0) {
          setActiveId((prev) =>
            list.some((b) => b.bracketId === prev) ? prev : list[0].bracketId
          );
        }
      }
    } catch (error) {
      console.error('Error loading brackets:', error);
      setBrackets([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading brackets...</p>
      </div>
    );
  }

  if (brackets.length === 0) {
    return (
      <div className="text-center py-5">
        <h5 className="text-muted">No brackets published yet</h5>
        <p className="text-muted">
          Brackets appear here once pool play wraps up and the tournament
          director publishes them
        </p>
      </div>
    );
  }

  const active = brackets.find((b) => b.bracketId === activeId) || brackets[0];
  const is3GG = active.bracketType === 'THREE_GG_DOUBLE_ELIMINATION';

  return (
    <div className="public-brackets-tab">
      {/* Only show a picker when there's more than one bracket in the division */}
      {brackets.length > 1 && (
        <Nav
          variant="pills"
          activeKey={active.bracketId}
          onSelect={(k) => setActiveId(Number(k))}
          className="mb-3"
        >
          {brackets.map((b) => (
            <Nav.Item key={b.bracketId}>
              <Nav.Link eventKey={b.bracketId}>{b.bracketName}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      )}

      {is3GG ? (
        <ThreeGGBracketView
          games={active.bracketGames || []}
          bracket={active}
          isAdmin={false}
        />
      ) : (
        <SingleElimBracketView
          games={active.bracketGames || []}
          bracket={active}
          isAdmin={false}
        />
      )}
    </div>
  );
};

export default PublicBracketsTab;
